'use client'

import Image from 'next/image'
import Link from 'next/link'
import { ArrowLeft, ArrowRight, Clock3, Heart, Lock } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { getLogoBrand } from '@/utils/getLogoBrand'
import { canOptimizeImageSource } from '@/lib/image-source'
import type { HomepageContent } from './PayloadHomepageHero'

export type HomepageOffer = {
  _id: string
  title: string
  subtitle: string
  imageUrl: string
  price: string
  installment: string
  slug?: { current?: string | null } | null
  brand?: string
  model?: string
  brandCode?: string
  fuel?: string
}

function formatEuro(value: string | number | undefined) {
  const digits = String(value ?? '').trim()
  if (!digits || !Number.isFinite(Number(digits))) return '—'
  return `${new Intl.NumberFormat('it-IT').format(Number(digits))} €`
}

function OfferCard({ offer, content }: { offer: HomepageOffer; content: NonNullable<HomepageContent['offersSection']> }) {
  const href = offer.slug?.current ? `/offerta-last-minute/${offer.slug.current}` : '/offerta-last-minute'
  const brand = offer.brand || offer.title
  const model = offer.model || offer.subtitle
  const logo = getLogoBrand(offer.brandCode || brand)
  const optimize = canOptimizeImageSource(offer.imageUrl)

  return (
    <article className="flex h-[419px] flex-col overflow-hidden rounded-[20px] border border-[#e3e9f1] bg-white md:h-[503px] md:rounded-[22px]">
      <div className="relative h-[180px] shrink-0 bg-gradient-to-b from-[#005aa9] to-[#0b1b33] md:h-[210px]">
        <div className="absolute inset-x-5 bottom-0 top-10 md:inset-x-5 md:top-[46px]">
          <Image src={offer.imageUrl} alt={`${brand} ${model}`} fill unoptimized={!optimize} sizes="(max-width: 767px) 246px, 260px" className="object-contain" />
        </div>
        <span className="absolute left-[14px] top-[14px] inline-flex items-center gap-[5px] rounded-full bg-[#d7192a] px-[11px] py-[5px] text-[11px] font-bold text-white md:left-4 md:top-4 md:gap-[6px] md:px-3 md:py-[6px] md:text-xs">
          <Clock3 aria-hidden="true" size={12} />{content.badgeLabel || 'Last minute'}
        </span>
        <span aria-hidden="true" className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white md:right-[14px] md:top-[14px] md:h-9 md:w-9">
          <Heart size={16} strokeWidth={1.6} className="text-[#0b1b33] md:h-[17px] md:w-[17px]" />
        </span>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 px-[18px] pb-[18px] pt-4 md:gap-4 md:px-[22px] md:pb-[22px] md:pt-5">
        <div className="flex min-h-10 items-center gap-2.5 md:min-h-[60px] md:gap-3">
          <Image src={logo} alt={`Logo ${brand}`} width={32} height={32} className="h-7 w-7 shrink-0 object-contain md:h-8 md:w-8" />
          <div className="min-w-0 font-bold">
            <p className="truncate text-xs text-[#5b6b82] md:text-[13px]">{brand}</p>
            <h3 className="line-clamp-2 text-[15px] leading-[1.3] text-[#0b1b33] md:text-[16px]">{model}</h3>
          </div>
        </div>

        <div className="flex gap-1.5 text-[11px] font-semibold text-[#5b6b82] md:gap-1.5">
          {offer.fuel && <span className="rounded-[7px] bg-[#f3f6fa] px-[9px] py-1">{offer.fuel}</span>}
          <span className="rounded-[7px] bg-[#f3f6fa] px-[9px] py-1">{content.readyLabel || 'Pronta consegna'}</span>
        </div>

        <div className="flex items-end justify-between border-t border-[#e3e9f1] pt-3 md:pt-4">
          <div>
            <p className="text-[11px] font-semibold text-[#5b6b82]">{content.priceLabel || 'Tua a'}</p>
            <p className="text-2xl font-extrabold tracking-[-0.36px] text-[#0b1b33] md:text-[28px]">{formatEuro(offer.price)}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-[#5b6b82]">{content.monthlyPrefix || 'oppure da'}</p>
            <p className="text-sm font-bold text-[#005aa9] md:text-base">{formatEuro(offer.installment)}/mese</p>
          </div>
        </div>

        <Link href={href} className="mt-auto flex h-11 items-center justify-center gap-2 rounded-[10px] bg-[#0b1b33] text-sm font-bold text-white transition-colors hover:bg-[#15345b] md:h-[47px]">
          <Lock aria-hidden="true" size={15} />{content.lockPriceLabel || 'Blocca il prezzo'}
        </Link>
      </div>
    </article>
  )
}

export default function PayloadLastMinuteOffers({ content, offers }: { content: HomepageContent; offers: HomepageOffer[] }) {
  const section = content.offersSection
  const sectionRef = useRef<HTMLElement>(null)
  const desktopTrackRef = useRef<HTMLDivElement>(null)
  const mobileTrackRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [autoplayActive, setAutoplayActive] = useState(true)
  const [isVisible, setIsVisible] = useState(false)
  const visibleOffers = offers
  const autoplaySeconds = Math.max(0, Number(section?.autoplaySeconds ?? 30))

  useEffect(() => {
    const sectionElement = sectionRef.current
    if (!sectionElement) return
    if (!('IntersectionObserver' in window)) {
      setIsVisible(true)
      return
    }

    const observer = new IntersectionObserver(([entry]) => setIsVisible(entry.isIntersecting), { threshold: 0.15 })
    observer.observe(sectionElement)
    return () => observer.disconnect()
  }, [])

  const scrollMobile = (index: number) => {
    mobileTrackRef.current?.scrollTo({ left: index * 300, behavior: 'smooth' })
    setActiveIndex(index)
  }
  const scrollDesktop = (direction: -1 | 1) => {
    desktopTrackRef.current?.scrollBy({ left: direction * 326, behavior: 'smooth' })
  }

  useEffect(() => {
    if (!autoplayActive || !isVisible || autoplaySeconds <= 0 || visibleOffers.length < 2) return

    const timerId = window.setInterval(() => {
      if (window.matchMedia('(min-width: 768px)').matches) {
        const track = desktopTrackRef.current
        if (!track || track.scrollWidth <= track.clientWidth + 1) return

        const maxScroll = track.scrollWidth - track.clientWidth
        if (track.scrollLeft >= maxScroll - 2) {
          track.scrollTo({ left: 0, behavior: 'smooth' })
        } else {
          track.scrollBy({ left: 326, behavior: 'smooth' })
        }
        return
      }

      scrollMobile((activeIndex + 1) % visibleOffers.length)
    }, autoplaySeconds * 1000)

    return () => window.clearInterval(timerId)
  }, [activeIndex, autoplayActive, autoplaySeconds, isVisible, visibleOffers.length])

  if (!section || !section.title) return null

  return (
    <section
      ref={sectionRef}
      className="bg-[#f3f6fa] py-14 md:py-24"
      aria-labelledby="homepage-offers-title"
      onPointerDownCapture={() => setAutoplayActive(false)}
      onClickCapture={() => setAutoplayActive(false)}
      onFocusCapture={() => setAutoplayActive(false)}
      onKeyDownCapture={() => setAutoplayActive(false)}
      onWheelCapture={() => setAutoplayActive(false)}
    >
      <div className="homepage-content-container">
        <div className="mb-6 flex items-end justify-between gap-6 md:mb-11">
          <div className="max-w-[620px]">
            <p className="mb-2 text-xs font-bold tracking-[1.44px] text-[#005aa9] md:mb-3 md:text-[13px] md:tracking-[1.56px]">{section.eyebrow}</p>
            <h2 id="homepage-offers-title" className="text-[30px] font-extrabold leading-[1.15] tracking-[-0.6px] text-[#0b1b33] md:text-[46px] md:leading-none md:tracking-[-1.15px]">
              {section.title.split(/(last minute)/i).map((part, index) => part.toLowerCase() === 'last minute' ? <span key={index} className="text-[#d7192a]">{part}</span> : part)}
            </h2>
            <p className="mt-2 hidden text-[17px] leading-[1.5] text-[#5b6b82] md:block">{section.descriptionDesktop}</p>
            <p className="mt-2 text-[15px] leading-[1.5] text-[#5b6b82] md:hidden">{section.descriptionMobile}</p>
          </div>

          <div className="hidden shrink-0 items-center gap-3 md:flex">
            {section.allOffersHref && <Link href={section.allOffersHref} className="mr-1 inline-flex items-center gap-2 text-[15px] font-bold text-[#005aa9]">{section.allOffersLabel}<ArrowRight aria-hidden="true" size={18} /></Link>}
            <button type="button" aria-label="Offerta precedente" onClick={() => scrollDesktop(-1)} className="flex h-12 w-12 items-center justify-center rounded-full border-[1.5px] border-[#e3e9f1] bg-white text-[#0b1b33]"><ArrowLeft size={20} /></button>
            <button type="button" aria-label="Offerta successiva" onClick={() => scrollDesktop(1)} className="flex h-12 w-12 items-center justify-center rounded-full bg-[#0b1b33] text-white"><ArrowRight size={20} /></button>
          </div>
        </div>

        {visibleOffers.length > 0 ? (
          <>
            <div ref={desktopTrackRef} onScroll={(event) => setActiveIndex(Math.min(visibleOffers.length - 1, Math.round(event.currentTarget.scrollLeft / 326)))} className="homepage-edge-track hidden snap-x snap-mandatory gap-6 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:flex">
              {visibleOffers.map((offer) => <div key={offer._id} className="w-[302px] shrink-0 snap-start"><OfferCard offer={offer} content={section} /></div>)}
            </div>
            <div ref={mobileTrackRef} onScroll={(event) => setActiveIndex(Math.min(visibleOffers.length - 1, Math.round(event.currentTarget.scrollLeft / 300)))} className="homepage-edge-track flex snap-x snap-mandatory gap-[14px] overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:hidden">
              {visibleOffers.map((offer) => <div key={offer._id} className="w-[286px] shrink-0 snap-start"><OfferCard offer={offer} content={section} /></div>)}
            </div>
          </>
        ) : <p className="rounded-xl bg-white p-6 text-[#5b6b82]">Nessuna offerta disponibile al momento.</p>}

        <div className="mt-6 flex items-center justify-between md:hidden">
          <div className="flex gap-1.5" aria-label="Paginazione offerte">
            {visibleOffers.map((offer, index) => <button key={offer._id} type="button" aria-label={`Vai all’offerta ${index + 1}`} aria-current={activeIndex === index ? 'true' : undefined} onClick={() => scrollMobile(index)} className={`h-1.5 rounded-full ${activeIndex === index ? 'w-[22px] bg-[#0b1b33]' : 'w-1.5 bg-[#c8d1dd]'}`} />)}
          </div>
          {section.allOffersHref && <Link href={section.allOffersHref} className="inline-flex items-center gap-2 text-[15px] font-bold text-[#005aa9]">{section.allOffersMobileLabel || 'Vedi tutte'}<ArrowRight size={18} /></Link>}
        </div>
      </div>
    </section>
  )
}
